import React, { useState, useRef, useEffect } from 'react'
import { FiSearch } from 'react-icons/fi'
import api from '../../utils/api'
import Spinner from '../Spinner'
import { useLocationCascade } from '../../hooks/useLocations'

const FilterWithSearchAndRegion = ({
  placeholder = "Search...",
  suggestions = [],
  search,
  setSearch,
  onSearch,
  view,
  setView,
  handleChange,
  hideMap,
  hideLocationFilters = false,
  suggestionsUrl = "",
  formatSuggestion = (item) => `${item.firstname} ${item.lastname}`
}) => {
  const [selectedRegion, setSelectedRegion] = useState('')
  const [selectedDistrict, setSelectedDistrict] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [dynamicSuggestions, setDynamicSuggestions] = useState([])
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false)
  const inputRef = useRef(null)

  const { regionNames, districts, communities, isLoading } = useLocationCascade(
    selectedRegion,
    selectedDistrict
  )

  // Fetch dynamic suggestions from backend
  useEffect(() => {
    if (!suggestionsUrl || !search || search.trim().length < 2) {
      setDynamicSuggestions([])
      return
    }

    const delayDebounceFn = setTimeout(async () => {
      setIsFetchingSuggestions(true)
      try {
        const response = await api.get(suggestionsUrl, {
          params: { search: search, page_size: 10 }
        })
        if (response?.data?.results) {
          const formatted = response.data.results.map(item => formatSuggestion(item))
          setDynamicSuggestions(formatted)
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error)
      } finally {
        setIsFetchingSuggestions(false)
      }
    }, 400) // 400ms debounce

    return () => clearTimeout(delayDebounceFn)
  }, [search, suggestionsUrl, formatSuggestion])

  // Combine static and dynamic suggestions
  const normalizedSearch = (search || "").toLowerCase()
  const allSuggestions = [
    ...suggestions.filter(
      (s) => s != null && String(s).toLowerCase().includes(normalizedSearch)
    ),
    ...dynamicSuggestions.filter((s) => s != null)
  ]
  // Remove duplicates
  const uniqueSuggestions = [...new Set(allSuggestions)]

  return (
    <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-14 gap-5 w-full'>
      {/* SEARCH */}
      <div className='flex flex-row gap-2'>
        <div className='w-full relative'>
          <input
            type="text"
            placeholder={placeholder}
            className="w-full bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-9 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
            value={search || ""}
            onChange={(e) => {
              setSearch && setSearch(e.target.value)
              onSearch && onSearch(e.target.value)
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 100)}
            ref={inputRef}
          />
          <FiSearch size={20} className='absolute left-2 top-1/2 -translate-y-1/2 text-black/70 dark:text-white/70' />
          {showSuggestions && uniqueSuggestions.length > 0 && (
            <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-secondaryDark rounded-md shadow-xl z-50 max-h-60 overflow-y-auto border border-gray-200 dark:border-border-dark">
              {isFetchingSuggestions && (
                <div className="px-4 py-2 flex items-center gap-2 text-sm text-gray-500">
                  <Spinner size="sm" /> <span>Searching...</span>
                </div>
              )}
              {uniqueSuggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark text-black dark:text-white text-sm"
                  onMouseDown={() => {
                    onSearch && onSearch(suggestion)
                    setShowSuggestions(false)
                    inputRef.current?.blur()
                  }}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => onSearch && onSearch(search)}
          className='bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex items-center justify-center py-2 rounded-md'>
          Search
        </button>
      </div>

      <div className='flex flex-col sm:flex-row md:flex-row gap-2 sm:items-center md:items-center'>
        {!hideLocationFilters && <>
          {hideMap
            ? (
              <></>
            )
            : viewOptions.map((item, index) => (
              <button
                key={index}
                onClick={() => setView(item?.value)}
                className={`bg-white dark:bg-dark ${view === item?.value ? "border border-yellowcolor text-yellowcolor" : " dark:bg-dark border dark:border-border-dark dark:text-white text-black"} px-2 flex items-center justify-center py-2 rounded-md`}
              >
                {item?.icon}
              </button>
            ))}

          <div className='w-full md:w-1/2'>
            <select
              name="region"
              value={selectedRegion}
              onChange={(e) => {
                const value = e.target.value
                setSelectedRegion(value)
                setSelectedDistrict('')
                handleChange?.(e)
              }}
              className="py-3 capitalize px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white">
              <option value="">Select Region</option>
              {isLoading && <option>Loading...</option>}
              {regionNames.map((item, index) => (
                <option className='capitalize' key={index} value={item}>{item}</option>
              ))}
            </select>
          </div>


          <div className='w-full md:w-1/2'>
            <select
              name="district"
              value={selectedDistrict}
              onChange={(e) => {
                setSelectedDistrict(e.target.value)
                handleChange?.(e)
              }}
              className="py-3 px-4 capitalize pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white">
              <option value="">Select District</option>
              {districts.map((item, index) => (
                <option className="capitalize" key={index} value={item?.name}>{item?.name}</option>
              ))}
            </select>
          </div>

          <div className='w-full md:w-1/2'>
            <select
              name="community"
              onChange={handleChange}
              className="py-3 px-4 capitalize pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white">
              <option value="">Select Community</option>
              {communities.map((item, index) => (
                <option className="capitalize" key={index} value={item}>{item}</option>
              ))}
            </select>
          </div>
        </>
        }
      </div>
    </div>
  )
}

const viewOptions = [
  {
    label: "Map",
    value: "map",
    icon: <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      color='currentColor'
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M22 8.99953V14.9995C22 17.4995 21.5 19.2495 20.38 20.3795L14 13.9995L21.73 6.26953C21.91 7.05953 22 7.95953 22 8.99953Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M21.73 6.27L6.26999 21.73C3.25999 21.04 2 18.96 2 15V9C2 4 4 2 9 2H15C18.96 2 21.04 3.26 21.73 6.27Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M20.3795 20.38C19.2495 21.5 17.4995 22 14.9995 22H8.99954C7.95954 22 7.05953 21.91 6.26953 21.73L13.9995 14L20.3795 20.38Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M6.23929 7.9807C6.91929 5.0507 11.3193 5.0507 11.9993 7.9807C12.3893 9.7007 11.3093 11.1607 10.3593 12.0607C9.66928 12.7207 8.5793 12.7207 7.8793 12.0607C6.9293 11.1607 5.83929 9.7007 6.23929 7.9807Z" stroke="currentColor" stroke-width="1.5" />
      <path d="M9.09412 8.69922H9.1031" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  },
  {
    label: "List",
    value: "list",
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 10H3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M21 6H3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M21 14H3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M21 18H3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  }
]


export default FilterWithSearchAndRegion
